const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const APP_VERSION = process.env.REACT_APP_APP_VERSION || process.env.npm_package_version || '0.1.0';

const isPosthogEnabled = Boolean(POSTHOG_KEY);
let initialized = false;

/**
 * Loads and initialises PostHog off the critical path.
 *
 * posthog-js is 142KB minified — the second-largest thing on this site after
 * React itself — and it used to be a static import evaluated before the first
 * render. Nothing above the fold depends on it, so it is now a dynamic import
 * that webpack emits as its own chunk and that nothing blocks on.
 *
 * The manual $pageview is captured on init, which is a beat later than it used
 * to be. PostHog stamps events at capture time, so the pageview timestamp
 * shifts by roughly the idle delay. That is the trade for not blocking the
 * render, and it is the right one for a portfolio.
 */
export function initPosthog() {
  if (!isPosthogEnabled || initialized) return Promise.resolve(null);
  initialized = true;

  return import(/* webpackChunkName: "posthog" */ 'posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: true,
        capture_pageview: false, // We trigger manual pageview below
        capture_pageleave: true,
        persistence: 'localStorage+cookie',
        session_recording: {
          maskAllInputs: true,
        },
        loaded: (ph) => {
          ph.register({
            environment: process.env.NODE_ENV,
            app: 'franco-cirulli-portfolio',
            app_version: APP_VERSION,
          });
        },
      });

      posthog.capture('$pageview');
      return posthog;
    })
    .catch(() => {
      // A blocked or failed analytics chunk must never surface to the visitor.
      initialized = false;
      return null;
    });
}

/**
 * Defers initPosthog until the browser is idle after load.
 *
 * The 2000ms timeout is a ceiling, not a target: requestIdleCallback can starve
 * indefinitely on a busy main thread, and a pageview that never fires is worse
 * than one that fires late.
 */
export function initPosthogWhenIdle() {
  if (!isPosthogEnabled) return;

  const schedule = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => initPosthog(), { timeout: 2000 });
    } else {
      window.setTimeout(initPosthog, 1200);
    }
  };

  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }
}

export { isPosthogEnabled };
